import React, { useState, useEffect } from "react";
import { RRule, rrulestr } from "rrule";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from "@mui/material";

// UI labels for weekdays
const WEEKDAYS = [
  { label: "Mon", value: 0 },
  { label: "Tue", value: 1 },
  { label: "Wed", value: 2 },
  { label: "Thu", value: 3 },
  { label: "Fri", value: 4 },
  { label: "Sat", value: 5 },
  { label: "Sun", value: 6 },
];
// RRule constants lookup
const WEEKDAY_CONSTS = {
  0: RRule.MO,
  1: RRule.TU,
  2: RRule.WE,
  3: RRule.TH,
  4: RRule.FR,
  5: RRule.SA,
  6: RRule.SU,
};

const ORDINALS = [
  { label: "1st", value: 1 },
  { label: "2nd", value: 2 },
  { label: "3rd", value: 3 },
  { label: "4th", value: 4 },
  { label: "Last", value: -1 },
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const FREQ_OPTIONS = [
  { label: "Daily", value: RRule.DAILY },
  { label: "Weekly", value: RRule.WEEKLY },
  { label: "Monthly", value: RRule.MONTHLY },
  { label: "Yearly", value: RRule.YEARLY },
];

// Parse an RRULE (and optional DTSTART) into our component state
function parseRRule(value) {
  try {
    const rule = rrulestr(value);
    const o = rule.origOptions;
    const base = {
      freq: o.freq ?? RRule.DAILY,
      interval: o.interval ?? 1,
      dtstart: o.dtstart ? new Date(o.dtstart) : new Date(),
      weeklyDays:
        o.freq === RRule.WEEKLY && Array.isArray(o.byweekday)
          ? o.byweekday.map((wd) => wd.weekday)
          : [],
      // monthMode + monthlyDay vs ordinal/weekday
      repeatOn: "bymonthday",
      monthlyDay: o.bymonthday,
      ordinal:
        Array.isArray(o.byweekday) && o.byweekday.length
          ? o.byweekday[0].n || 1
          : 1,
      weekday:
        Array.isArray(o.byweekday) && o.byweekday.length
          ? o.byweekday[0].weekday
          : 1,
      yearlyMonth: o.bymonth,
    };
    if (base.freq === RRule.MONTHLY || base.freq === RRule.YEARLY) {
      if (Array.isArray(o.bymonthday) && o.bymonthday.length) {
        base.repeatOn = "bymonthday";
      } else if (Array.isArray(o.byweekday) && o.byweekday.length) {
        base.repeatOn = "byweekday";
      }
    }
    return base;
  } catch {
    return {
      freq: RRule.DAILY,
      interval: 1,
      dtstart: new Date(),
      weeklyDays: [],
      repeatOn: "bymonthday",
      monthlyDay: 1,
      ordinal: 1,
      weekday: 1,
      yearlyMonth: 1,
    };
  }
}

// Build an RFC‐compliant RRULE+DTSTART string from state
function buildRRuleFromState(s) {
  const opts = {
    freq: s.freq,
    interval: s.interval,
    dtstart: s.dtstart,
  };
  if (s.freq === RRule.WEEKLY && s.weeklyDays.length) {
    opts.byweekday = s.weeklyDays.map((d) => WEEKDAY_CONSTS[d]);
  }
  if (s.freq === RRule.MONTHLY || s.freq === RRule.YEARLY) {
    if (s.repeatOn === "bymonthday") {
      opts.bymonthday = [s.monthlyDay];
    } else {
      opts.byweekday = [WEEKDAY_CONSTS[s.weekday].nth(s.ordinal)];
    }
  }
  if (s.freq === RRule.YEARLY) {
    opts.bymonth = [s.yearlyMonth];
  }
  return new RRule(opts).toString();
}
export function defaultRRuleString() {
  const now = new Date();
  const dtString = now.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  return `DTSTART:${dtString}\nRRULE:FREQ=DAILY;INTERVAL=1`;
}

export default function RRuleEditor({ value = "", onChange }) {
  // component state
  const [state, setState] = useState(() => parseRRule(value));

  // sync when `value` prop changes from parent
  useEffect(() => {
    setState(parseRRule(value));
  }, [value]);

  // helper: merge patch → new state + emit onChange
  const updateState = (patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };
      onChange?.(buildRRuleFromState(next));
      return next;
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Removed the “Starts at” field as requested */}

      {/* Frequency */}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Frequency</InputLabel>
        <Select
          value={state.freq ?? ""}
          label="Frequency"
          onChange={(e) => {
            const freq = Number(e.target.value);
            const patch = { freq };
            // clear weekly if switching away
            if (freq !== RRule.WEEKLY) patch.weeklyDays = [];
            updateState(patch);
          }}
        >
          {FREQ_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Interval */}
      <TextField
        label="Every"
        type="number"
        fullWidth
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            min: 1,
          },
        }}
        helperText={`Every ${state.interval} ${
          state.freq === RRule.DAILY
            ? "day(s)"
            : state.freq === RRule.WEEKLY
            ? "week(s)"
            : state.freq === RRule.MONTHLY
            ? "month(s)"
            : "year(s)"
        }`}
        value={state.interval ?? ""}
        onChange={(e) =>
          updateState({
            interval: Math.max(1, Number(e.target.value) || 1),
          })
        }
      />

      {/* Weekly days */}
      {state.freq === RRule.WEEKLY && (
        <ToggleButtonGroup
          value={state.weeklyDays ?? []}
          onChange={(_, days) => updateState({ weeklyDays: days })}
          fullWidth
          sx={{ mb: 2 }}
        >
          {WEEKDAYS.map((d) => (
            <ToggleButton key={d.value} value={d.value}>
              {d.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}

      {/* Monthly / Yearly extras */}
      {(state.freq === RRule.MONTHLY || state.freq === RRule.YEARLY) && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Repeat on
          </Typography>

          <ToggleButtonGroup
            value={state.repeatOn}
            exclusive
            onChange={(_, mode) => mode && updateState({ repeatOn: mode })}
            sx={{ mb: 2 }}
          >
            <ToggleButton value="bymonthday">Day of Month</ToggleButton>
            <ToggleButton value="byweekday">Weekday Ordinal</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {state.repeatOn === "bymonthday" ? (
              <TextField
                label="Day"
                type="number"
                value={state.monthlyDay}
                sx={{ maxWidth: "75px" }}
                onChange={(e) =>
                  updateState({
                    monthlyDay: Math.min(
                      31,
                      Math.max(1, Number(e.target.value) || 1)
                    ),
                  })
                }
              />
            ) : (
              <>
                <FormControl>
                  <InputLabel>Ordinal</InputLabel>
                  <Select
                    value={state.ordinal ?? ""}
                    label="Ordinal"
                    onChange={(e) =>
                      updateState({ ordinal: Number(e.target.value) })
                    }
                  >
                    {ORDINALS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl>
                  <InputLabel>Weekday</InputLabel>
                  <Select
                    value={state.weekday ?? ""}
                    label="Weekday"
                    onChange={(e) =>
                      updateState({ weekday: Number(e.target.value) })
                    }
                  >
                    {WEEKDAYS.map((d) => (
                      <MenuItem key={d.value} value={d.value}>
                        {d.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {state.freq === RRule.YEARLY && (
              <FormControl>
                <InputLabel>Month</InputLabel>
                <Select
                  value={state.yearlyMonth ?? ""}
                  label="Month"
                  onChange={(e) =>
                    updateState({ yearlyMonth: Number(e.target.value) })
                  }
                  sx={{ minWidth: "100px" }}
                >
                  {MONTHS.map((m, i) => (
                    <MenuItem key={m} value={i + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
